import React, { useState } from 'react';
import { Shield, AlertTriangle, FileText, Scale, Target, Zap, Award, TrendingUp, CheckCircle, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import RegistrationModal from '../../components/RegistrationModal';
import SEO from '../../components/SEO';
import ServicePricing from '../../components/ServicePricing';
import CTASection from '../../components/CTASection';

const CopyrightEnforcementPage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [modalMode, setModalMode] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const openModal = (mode) => {
    setModalMode(mode);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setModalMode(null);
  };

  const enforcementStrategies = [
    {
      icon: FileText,
      title: "Cease & Desist Letters",
      description: "Professional demand letters that establish your rights and request immediate cessation of infringing activity with minimal legal costs."
    },
    {
      icon: Target,
      title: "DMCA Takedown Notices",
      description: "Swift removal of infringing content from websites, social media platforms, and search engines under Digital Millennium Copyright Act provisions."
    },
    {
      icon: Scale,
      title: "Settlement Negotiations",
      description: "Strategic negotiation to resolve disputes efficiently, securing monetary compensation and stopping infringement without costly litigation."
    },
    {
      icon: Shield,
      title: "Litigation Support",
      description: "Comprehensive lawsuit preparation and representation in federal court for serious infringement cases requiring judicial intervention."
    },
    {
      icon: Zap,
      title: "Expedited Actions",
      description: "Emergency preliminary injunctions and temporary restraining orders to immediately halt damaging infringement."
    },
    {
      icon: Award,
      title: "Damages Recovery",
      description: "Pursuit of actual damages, infringer's profits, or statutory damages up to $150,000 per work, plus attorney's fees."
    }
  ];

  const enforcementTypes = [
    {
      title: "Online Infringement",
      description: "Combat digital piracy, unauthorized downloads, and content theft across websites, social media, and file-sharing platforms.",
      examples: ["Website content theft", "Social media copying", "Unauthorized streaming", "File sharing networks"]
    },
    {
      title: "Commercial Infringement",
      description: "Address unauthorized commercial use of your works by businesses, competitors, and commercial entities.",
      examples: ["Counterfeit products", "Unauthorized merchandise", "Commercial reproductions", "Business exploitation"]
    },
    {
      title: "Print & Publishing",
      description: "Protect against unauthorized reproduction, distribution, or adaptation of written works and publications.",
      examples: ["Book piracy", "Article plagiarism", "Unauthorized translations", "Print reproductions"]
    },
    {
      title: "Software & Code",
      description: "Enforce rights against software piracy, code theft, and unauthorized distribution of digital products.",
      examples: ["Software piracy", "Code copying", "License violations", "App cloning"]
    }
  ];

  const process = [
    {
      step: "1",
      title: "Infringement Assessment",
      description: "We analyze the infringement, document evidence, and assess the strength of your case and potential remedies.",
      timeline: "1-2 days"
    },
    {
      step: "2",
      title: "Strategy & Demand",
      description: "Create a tailored enforcement strategy and send professionally drafted cease and desist letters or DMCA notices to infringers.",
      timeline: "2-5 days"
    },
    {
      step: "3",
      title: "Resolution & Recovery",
      description: "Negotiate settlements, pursue damages, or initiate litigation if necessary to protect your rights and recover losses.",
      timeline: "Varies"
    }
  ];

  const packages = [
    {
      name: "Essential Enforcement",
      price: "$499",
      description: "Basic protection response",
      features: [
        "Infringement analysis",
        "1 cease & desist letter",
        "DMCA takedown notice",
        "Initial consultation",
        "Email support",
        "30-day monitoring"
      ],
      popular: false
    },
    {
      name: "Professional Enforcement",
      price: "$999",
      description: "Comprehensive protection",
      features: [
        "Everything in Essential",
        "Up to 3 cease & desist letters",
        "Multiple DMCA notices",
        "Settlement negotiation",
        "90-day monitoring",
        "Priority support",
        "Damage assessment"
      ],
      popular: true
    },
    {
      name: "Enterprise Enforcement",
      price: "$2,499",
      description: "Full-service protection",
      features: [
        "Everything in Professional",
        "Unlimited cease & desist letters",
        "Ongoing monitoring (6 months)",
        "Litigation preparation",
        "Expert testimony support",
        "Dedicated enforcement team",
        "24/7 rapid response"
      ],
      popular: false
    }
  ];

  const faqs = [
    {
      question: "What is copyright enforcement and when do I need it?",
      answer: "Copyright enforcement is the process of stopping unauthorized use of your copyrighted works and recovering damages from infringers. You need enforcement when someone copies, distributes, displays, or creates derivative works from your content without permission. This includes online piracy, plagiarism, unauthorized commercial use, or any violation of your exclusive copyright rights. Early action is crucial to minimize damage and maximize recovery."
    },
    {
      question: "How much does copyright enforcement cost?",
      answer: "Costs vary based on the enforcement strategy. Simple cease and desist letters start around $500, while DMCA takedown notices can be included in monitoring services. Settlement negotiations typically range from $1,000-$5,000. Full litigation can cost $10,000-$100,000+ but may be recovered through attorney's fees provisions if you have a registered copyright. We offer fixed-fee packages to provide cost certainty for most enforcement actions."
    },
    {
      question: "What is a DMCA takedown notice?",
      answer: "A DMCA (Digital Millennium Copyright Act) takedown notice is a legal demand to remove infringing content from online platforms. When properly filed, it requires websites, search engines, and hosting providers to remove or disable access to the infringing material. Most platforms comply within 24-48 hours to maintain their safe harbor protection. This is often the fastest and most cost-effective way to remove online infringement."
    },
    {
      question: "Can I sue for copyright infringement without registration?",
      answer: "No, you must have a registered copyright before filing a lawsuit in federal court. This is why we always recommend early registration. However, you can still send cease and desist letters and DMCA notices without registration. If infringement is ongoing, we can expedite your registration (5-7 business days) to enable litigation if necessary."
    },
    {
      question: "What damages can I recover in copyright infringement?",
      answer: "With a registered copyright, you can recover: (1) actual damages and infringer's profits, (2) statutory damages of $750-$30,000 per work ($150,000 for willful infringement), and (3) attorney's fees and costs. You don't need to prove actual damages to claim statutory damages, making enforcement economically viable even for smaller cases. The threat of these significant damages often leads to favorable settlements."
    },
    {
      question: "How long does it take to stop infringement?",
      answer: "Timeline varies by enforcement method. DMCA takedowns typically succeed within 24-48 hours. Cease and desist letters often resolve within 1-4 weeks through voluntary compliance or settlement. If litigation is necessary, preliminary injunctions can be obtained in weeks, while full trials take 12-24 months. We recommend the fastest appropriate action based on the severity and nature of infringement."
    },
    {
      question: "What if the infringer is overseas?",
      answer: "International enforcement is challenging but not impossible. We can send cease and desist letters internationally and file DMCA notices with U.S.-based platforms hosting the content regardless of the infringer's location. For significant commercial infringement, we can pursue legal action through international treaties and foreign court systems. We assess the practicality and cost-effectiveness of international enforcement on a case-by-case basis."
    },
    {
      question: "Should I send a cease and desist letter or file a lawsuit?",
      answer: "Start with a cease and desist letter in most cases. It's faster, cheaper, and often effective—many infringers comply immediately when confronted with legal claims. Lawsuits are reserved for serious infringement where the infringer refuses to stop, substantial damages occurred, or you need a legal precedent. We analyze your situation and recommend the most effective strategy balancing cost, time, and desired outcomes."
    },
    {
      question: "What evidence do I need to prove copyright infringement?",
      answer: "You need: (1) proof of your copyright ownership (registration certificate or creation evidence), (2) evidence of the infringing use (screenshots, URLs, downloads, purchases), and (3) demonstration that the infringing work copies substantial protected elements. We help you collect, organize, and preserve evidence to build the strongest possible case. Proper documentation is crucial for successful enforcement."
    },
    {
      question: "Can I enforce copyright against fair use?",
      answer: "Fair use is a legal defense to copyright infringement allowing limited use without permission for purposes like commentary, criticism, news reporting, teaching, or parody. However, fair use is narrowly applied and fact-specific. Many claimed 'fair uses' don't qualify legally. We analyze whether the use truly qualifies as fair use or whether you have grounds for enforcement. Even if fair use might apply, cease and desist letters often succeed because infringers don't want the cost and uncertainty of litigation."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO 
        title="Copyright Enforcement Services - Stop Infringement & Protect Your Rights"
        description="Stop unauthorized use of your creative works. Professional enforcement, content removal, and damage recovery."
        keywords="copyright enforcement, stop infringement, DMCA takedown, cease and desist, copyright protection, piracy prevention"
      />

      {/* Hero Section */}
      <section className="relative text-white py-20 overflow-hidden">
            <img src="https://images.unsplash.com/photo-1589994965851-a8f479c573a9?w=900w=1600&q=80q=50" alt="" loading="eager" fetchpriority="high" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#2F1A8E]/90 via-[#1a0f5e]/85 to-[#0d0830]/90"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF035B]/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#FF035B]/5 rounded-full blur-2xl"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#FF035B]/20 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <AlertTriangle className="w-4 h-4" />
                <span>Stop Infringement Now</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Powerful Copyright Enforcement to Protect Your Creative Works
              </h1>
              <p className="text-xl mb-8 text-gray-300">
                Stop unauthorized use of your copyrighted content. From DMCA takedowns to federal litigation, we enforce your rights and recover damages from infringers.
              </p>
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => openModal('registration')}
                  className="bg-[#FF035B] hover:bg-[#2F1A8E] text-white px-8 py-4 rounded-lg font-semibold transition-all hover:shadow-xl hover:scale-105"
                >
                  Report Infringement
                </button>
                <button 
                  onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
                  className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all"
                >
                  Speak with Attorney
                </button>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>24-48 Hour Response</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>No Win, Reduced Fee</span>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
              <h3 className="text-lg font-bold mb-6">Why Act Quickly?</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Revenue Loss</strong>
                    <span className="text-gray-300">Every day of infringement means lost sales and market share</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Brand Damage</strong>
                    <span className="text-gray-300">Unauthorized use can tarnish your reputation and confuse customers</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Evidence Preservation</strong>
                    <span className="text-gray-300">Infringing content may be removed or modified, making it harder to prove</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Enforcement Strategies */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Enforcement Strategies
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tailored approaches to stop infringement and protect your rights
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {enforcementStrategies.map((strategy, index) => (
              <div key={index} className="group bg-white hover:bg-gradient-to-br hover:from-[#FF035B] hover:to-[#2F1A8E] rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:scale-105 cursor-pointer hover:shadow-xl transition-all border border-gray-100">
                <strategy.icon className="w-12 h-12 text-[#FF035B] group-hover:text-white transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-white transition-colors duration-500 mb-3">{strategy.title}</h3>
                <p className="text-gray-600 group-hover:text-white/90 transition-colors duration-500 leading-relaxed group-hover:text-white/90 transition-colors duration-500">{strategy.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Infringement Types */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Types of Infringement We Address
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Protecting your rights across all platforms and formats
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {enforcementTypes.map((type, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all">
                <h3 className="text-lg font-bold text-gray-900 group-hover:text-white transition-colors duration-500 mb-3">{type.title}</h3>
                <p className="text-gray-600 group-hover:text-white/90 transition-colors duration-500 mb-6">{type.description}</p>
                <div className="space-y-2">
                  {type.examples.map((example, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-gray-700">
                      <div className="w-1.5 h-1.5 bg-[#FF035B] rounded-full"></div>
                      {example}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Enforcement Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Swift, strategic action to protect your rights
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] text-white rounded-xl p-6 shadow-lg">
                  <div className="text-4xl font-bold mb-2 opacity-50">{item.step}</div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-sm mb-4 text-white/90">{item.description}</p>
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Clock className="w-4 h-4" />
                    {item.timeline}
                  </div>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-[#FF035B] to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <ServicePricing showPrices={false} title="Copyright Enforcement Services" subtitle="Get a personalized quote for your specific needs" />


      {/* FAQ */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about copyright enforcement
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-all"
                >
                  <span className="font-semibold text-gray-900 pr-8">{faq.question}</span>
                  {openFAQ === index ? (
                    <ChevronUp className="w-5 h-5 text-[#FF035B] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFAQ === index && (
                  <div className="px-8 pb-6">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Don't Let Infringers Profit from Your Work
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Take action today to stop unauthorized use and recover what's rightfully yours. Our enforcement experts are ready to protect your rights.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button 
              onClick={() => openModal('registration')}
              className="bg-white text-[#FF035B] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all hover:shadow-xl"
            >
              Report Infringement Now
            </button>
            <button 
              onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#FF035B] transition-all"
            >
              Schedule Consultation
            </button>
          </div>
        </div>
      </section>

      <CTASection />

      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType="registration"
      />
    </div>
  );
};

export default CopyrightEnforcementPage;
